/**
 * Fenêtre pendant laquelle une coupure récente explique encore un rejet tardif du SDK.
 *
 * `peerConnectionTimeout` vaut 15 s côté LiveKit : une négociation lancée juste avant la coupure
 * n'échoue qu'à l'échéance de ce délai fixe, quand le signal est déjà perdu — voire déjà repris.
 * Le double laisse de la marge sans effacer un échec qui n'aurait aucun rapport avec la coupure.
 */
const RECENT_DEGRADATION_MS = 30_000;

let degraded = false;
let lastDegradedAt = Number.NEGATIVE_INFINITY;

/**
 * État de la connexion à la salle, lisible depuis le rapporteur global (`main.tsx`), qui vit hors de
 * l'arbre React et n'a pas d'autre accès à la salle.
 *
 * Volontairement sans import de `livekit-client` : `main.tsx` lit ce module, et le SDK doit rester
 * dans le chunk de la visioconférence. C'est le composant de la salle qui alimente cet état.
 *
 * « Dégradé » veut dire **perdu après avoir été établi** (reprise en cours, déconnexion) — jamais
 * la connexion initiale. Compter `Connecting` taisait les échecs de négociation des trente
 * premières secondes de séance, précisément ceux du mur UDP/NAT qu'on tient à voir.
 */
export function setLiveConnectionDegraded(isDegraded: boolean): void {
  degraded = isDegraded;
  if (isDegraded) lastDegradedAt = Date.now();
}

/**
 * La connexion à la salle est-elle coupée, ou l'était-elle à l'instant ?
 *
 * Sert à départager un échec de négociation **consécutif** à une coupure (déjà signalée par la
 * machinerie de déconnexion, et rattrapée par la reprise) d'un échec survenu sur une salle
 * parfaitement connectée — le mur UDP/NAT, qui lui reste un vrai signal.
 *
 * L'horodatage survit au démontage de la salle : un rejoin silencieux remonte `LiveKitRoom` juste
 * après la coupure, et c'est pendant ce remontage que la négociation de l'ancienne salle expire.
 */
export function isLiveConnectionDegraded(): boolean {
  return degraded || Date.now() - lastDegradedAt < RECENT_DEGRADATION_MS;
}
